/*
#############################################################################
# Copyright (c) 2022-2023 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
#
# NVIDIA CORPORATION, its affiliates and licensors retain all intellectual
# property and proprietary rights in and to this material, related
# documentation and any modifications thereto. Any use, reproduction,
# disclosure or distribution of this material and related documentation
# without an express license agreement from NVIDIA CORPORATION or
# its affiliates is strictly prohibited.
###############################################################################
*/

/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { useSelector } from "react-redux";
import { Routes, Route, Navigate } from "react-router-dom";
import { ReadyState } from "react-use-websocket";
import { ErrorBoundary } from "react-error-boundary";
import FoodCategories from "./features/item-categories/itemCategories";
import TopBar from "./features/top-bar/TopBar";
import TouchMenu from "./pages/TouchMenu";
import Error404 from "./pages/ErrorPages";
import ItemRecommendation from "./pages/ItemRecommendation";
import FilteredItems from "./pages/SpeechMenu";
import ItemCustomization from "./pages/ItemCustomization";
import WebSocketConnection from "./connection/websocket/websocket";
import Webrtc from "./connection/webrtc/webrtc-sender-receiver/WebrtcSenderReceiver";
import WaitingMessage from "./features/waiting-message/waitingMessage";
import RecommendedItemBar from "./features/item-recommendation/RecommendedItemBar";
import FilteredItemsBar from "./features/filtered-items/FilteredItemsBar";
import WelcomeScreen from "./pages/WelcomeScreen";
import Cart from "./features/cart/Cart";
import ErrorFallback from "./features/error/ErrorFallback";
import AsrStateText from "./features/asrText/AsrText";
import { isPortraitMode } from "./utils/utils";
import backGroundImage from "./trianglify.svg";
import ngcBackGroundImage from "./ngc-background.png";
import CustomScreen from "./pages/CustomScreen";

/* Make websocket connection and show the UI if connection is successful */
function App({receivedWebsocketMessage}) {
  const [portraitModeStatus, setPortraitModeStatus] = useState();
  const [micEnabled, setMicEnabled] = useState(true);
  WebSocketConnection();
  const readyState = useSelector((state) => state.uuid.status);
  const connectionStatus = {
    [ReadyState.CONNECTING]: "Connecting",
    [ReadyState.OPEN]: "Open",
    [ReadyState.CLOSING]: "Closing",
    [ReadyState.CLOSED]: "Closed",
    [ReadyState.UNINSTANTIATED]: "Uninstantiated",
  }[readyState];

  useEffect(() => {
    console.log("Websocket is currently", connectionStatus);
  }, [connectionStatus]);

  const handleMicrophoneUpdate = (newValue) => {
    setMicEnabled(newValue);
  };

  /**
   * TODO: When landscape design is ready remove the below non-standard method and switch
   * the UI between landscape and portrait design based on screen type
   */
  const handleWindowSizeChange = () => {
    const pMode = isPortraitMode();
    console.log("pMode", pMode);
    const body = document.getElementsByTagName("BODY")[0];
    const html = document.getElementsByTagName("HTML")[0];
    if (pMode) {
      body.style.maxWidth = "3000px";
      html.style.maxWidth = "3000px";
      html.style.zoom = 1;
      html.style.backgroundImage = `url(${ngcBackGroundImage})`;
      body.style.backgroundImage = `url(${ngcBackGroundImage})`;
      html.style.backgroundSize = "cover";
      body.style.backgroundSize = "cover";
      html.style.backgroundAttachment = "fixed";
      body.style.backgroundAttachment = "fixed";
      console.log("removed max-width");
    } else {
      body.style.maxWidth = "1080px";
      html.style.maxWidth = "1080px";
      html.style.zoom = 0.75;
      html.style.backgroundImage = `url(${backGroundImage})`;
      body.style.backgroundImage = `url(${backGroundImage})`;
      html.style.backgroundSize = "cover";
      body.style.backgroundSize = "cover";
      html.style.backgroundAttachment = "fixed";
      body.style.backgroundAttachment = "fixed";
      console.log("added max-width");
    }
    setPortraitModeStatus(pMode);
  };

  useEffect(() => {
    handleWindowSizeChange();
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  if (typeof VST_IP !== "undefined") {
    // eslint-disable-next-line no-undef
    console.debug("VST IP", VST_IP);
  }
  if (typeof VST_PORT !== "undefined") {
    // eslint-disable-next-line no-undef
    console.debug("VST PORT", VST_PORT);
  }

  return (
    <div>
      <div className='App'>
        <div
          className={portraitModeStatus ? "" : "device-frame raisedbox"}
          id='main-container'
          style={{ backgroundImage: ngcBackGroundImage }}
        >
          <Grid container>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <TopBar 
                micEnabled={micEnabled}
                handleMicrophoneUpdate={handleMicrophoneUpdate}
              />
              <Webrtc
                pMode={portraitModeStatus}
                micEnabled={micEnabled}
                receivedWebsocketMessage={receivedWebsocketMessage}
              />
            </ErrorBoundary>
            
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <WaitingMessage />
              <RecommendedItemBar />
              <FilteredItemsBar />
              <FoodCategories />
              <AsrStateText />
              <Routes>
                <Route
                  path='/'
                  element={<Navigate replace to='/WELCOME_VIEW' />}
                />
                <Route
                  path='/index.html'
                  element={<Navigate replace to='/WELCOME_VIEW' />}
                />
                <Route exact path='/VARIETY_VIEW' element={<TouchMenu />} />
                <Route
                  exact
                  path='/VARIETY_VIEW/:category'
                  element={<TouchMenu />}
                />
                <Route exact path='/CART_VIEW' element={<Cart />} />
                <Route
                  path='/ITEM_VIEW/:itemId'
                  exact
                  element={<ItemCustomization />}
                />
                <Route exact path='/WELCOME_VIEW' element={<WelcomeScreen />} />
                <Route
                  exact
                  path='/RECOMMENDED_VIEW'
                  element={<ItemRecommendation />}
                />
                <Route
                  exact
                  path='/FILTER_VIEW/variety'
                  element={<FilteredItems />}
                />
                <Route
                  exact
                  path='/FILTER_VIEW/category/:filteredCategories'
                  element={<FilteredItems />}
                />
                <Route
                  path='/CUSTOM_VIEW'
                  exact
                  element={<CustomScreen />}
                />
                <Route path='*' element={<Error404 />} />
              </Routes>
            </ErrorBoundary>
          </Grid>
        </div>
      </div>
    </div>
  );
}

export default App;